import { useState, useEffect, useRef, useCallback } from 'react';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useUser } from '@/contexts/UserContext';
import { APIRoutes } from '@/services/api.routes';
import type { DayTradingSessionID } from '@/models/ids';

interface UseDayTradingLiveSessionReturn {
  liveSessionId: DayTradingSessionID | null;
  loading: boolean;
}

const POLL_INTERVAL = 30_000;

export function useDayTradingLiveSession(): UseDayTradingLiveSessionReturn {
  const [liveSessionId, setLiveSessionId] = useState<DayTradingSessionID | null>(null);
  const [loading, setLoading] = useState(true);
  const { hasFeature } = useFeatureAccess();
  const { isAdmin } = useUser();
  const shouldPoll = hasFeature('dayTrading') || isAdmin;
  const prevIdRef = useRef<DayTradingSessionID | null>(null);

  const fetchLiveSession = useCallback(async () => {
    try {
      const response = await APIRoutes.listDayTradingSessions({ open: true, limit: 1 });
      const id = response.sessions.length > 0 ? response.sessions[0].id : null;
      if (id !== prevIdRef.current) {
        prevIdRef.current = id;
        setLiveSessionId(id);
      }
    } catch {
      // Silently fail - polling will retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!shouldPoll) {
      setLoading(false);
      return;
    }

    fetchLiveSession();
    const interval = setInterval(fetchLiveSession, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [shouldPoll, fetchLiveSession]);

  return { liveSessionId, loading };
}
